import React, { ReactElement, ReactNode } from 'react';
import cx from 'classnames';
import { ReportEntity, SprintEntity, WorkDayUserEntity } from '@data';
import { useTranslation } from 'react-i18next';
import { dayKey, fullDate, isWeekendDate, useLeavingRows } from '@app/utils';
import { FULL_WORKDAY_MINUTES } from '@app/constants/app';
import SprintDayReport from '@app/components/shared/sprintDayReport';
import CollapseIn from '@app/components/core/collapse-in';
import SprintAccordion, { sprintTotalHours } from './SprintAccordion';

export interface SprintOverviewProps {
  workDaysData: WorkDayUserEntity[];
  todayDate: Date;
  showAddForm: (workDay: string, productIds?: string[]) => void;
  showEditForm: (report: ReportEntity) => void;
  showDeleteForm: (report: ReportEntity) => void;
  /** The sprints of the year on show — Overview pages a year at a time. */
  sprints?: SprintEntity[];
  /** Year pager, rendered below the list and on an empty year too, so you can page back out of it. */
  pager?: ReactNode;
  className?: string;
  /** Days a full workday was logged on across every product, so a product filter cannot unsettle them. */
  fullDays?: string[];
  /** Days the member has called done, as `yyyy-mm-dd`. */
  finishedDays?: string[];
  /** First day a mark can still be taken off, as `yyyy-mm-dd`: the To report window's own start. */
  undoableFrom?: string;
  onSetFinished?: (workday: string, finished: boolean) => void;
}

/**
 * The sprint totals share one column, sized to the widest total on the page. Tabular figures give every
 * digit the same width, so the most digits wins and a decimal point only adds a sliver on top.
 */
const totalWidth = (hours: number): number => {
  const text = String(hours);
  return text.replace(/\D/g, '').length * 2 + (text.includes('.') ? 1 : 0);
};

/** The work that is settled: every sprint that is over, and the days of the running one you called finished. */
const SprintOverview = ({
  workDaysData,
  todayDate,
  showAddForm,
  showEditForm,
  showDeleteForm,
  sprints,
  pager,
  className,
  fullDays,
  finishedDays,
  undoableFrom,
  onSetFinished,
}: SprintOverviewProps): ReactElement => {
  const { t } = useTranslation();

  // Strictly the sprint running today, not getCurrentSprint's "most recently started" fallback: on a year
  // that is entirely past, the fallback promotes December's sprint as though it were live.
  const today = dayKey(todayDate);
  const currentSprint = (sprints ?? []).find(
    sprint => fullDate(sprint.startDate) <= today && today <= fullDate(sprint.endDate),
  );

  const finished = React.useMemo(() => new Set(finishedDays ?? []), [finishedDays]);
  const full = React.useMemo(() => (fullDays ? new Set(fullDays) : undefined), [fullDays]);
  // The same bound SprintAccordion applies, so a loose row cannot outlive the window the grouped ones respect.
  const canUndo = (date: string): boolean => finished.has(date) && (!undoableFrom || date >= undoableFrom);

  const groups = (sprints ?? [])
    .slice()
    .sort((a, b) => (fullDate(a.startDate) < fullDate(b.startDate) ? 1 : -1))
    .map(sprint => {
      const days = workDaysData.filter(
        day => fullDate(sprint.startDate) <= day.date && day.date <= fullDate(sprint.endDate),
      );

      // Settled is what To report drops, so between the two tabs every reported day is on one.
      const settled = (day: WorkDayUserEntity): boolean =>
        finished.has(day.date) ||
        (full ? full.has(day.date) : day.totalMinutes >= FULL_WORKDAY_MINUTES) ||
        isWeekendDate(day.date);

      // The running sprint lists only its settled days; a sprint that is over is settled entire.
      return { sprint, days, listedDays: sprint.id === currentSprint?.id ? days.filter(settled) : days };
    });

  const visibleGroups = groups.filter(group => group.listedDays.some(day => day.reports.length > 0));

  // Days falling inside no known sprint are still listed, so nothing is hidden.
  const allSprintDates = new Set(groups.flatMap(group => group.days.map(day => day.date)));
  const ungrouped = workDaysData.filter(day => !allSprintDates.has(day.date) && day.reports.length > 0);

  // Undoing the running sprint's last settled day empties its card, which would otherwise unmount before the
  // row inside it could collapse. Held here, the card leaves the way a row does.
  const { rows: shownGroups, isLeaving: isGroupLeaving } = useLeavingRows(visibleGroups, group => group.sprint.id);

  const hasAnything = shownGroups.length > 0 || ungrouped.length > 0;

  const columnTotalHours = shownGroups
    .map(group => sprintTotalHours(group.days))
    .reduce((widest, hours) => (totalWidth(hours) > totalWidth(widest) ? hours : widest), 0);

  // The newest sprint over opens, so the page lands on something rather than a stack of closed headers.
  const renderSprint = ({
    sprint,
    days,
    listedDays,
  }: {
    sprint: SprintEntity;
    days: WorkDayUserEntity[];
    listedDays: WorkDayUserEntity[];
  }): ReactElement => (
    <SprintAccordion
      sprint={sprint}
      days={days}
      listedDays={listedDays}
      isOpen={sprint.id === shownGroups[0]?.sprint.id}
      todayDate={todayDate}
      showAddForm={showAddForm}
      showEditForm={showEditForm}
      showDeleteForm={showDeleteForm}
      columnTotalHours={columnTotalHours}
      finishedDays={finishedDays}
      fullDays={fullDays}
      undoableFrom={undoableFrom}
      onSetFinished={onSetFinished}
    />
  );

  return (
    <div className={cx('flex w-full flex-1 flex-col', className)}>
      {!hasAnything ? (
        <p className="py-6 text-center text-sm text-muted-foreground">{t('overview.nothingCompleted')}</p>
      ) : (
        <>
          {shownGroups.map(group => (
            <CollapseIn key={group.sprint.id} show={!isGroupLeaving(group)} className="group/sprint">
              {/* The gap sits inside the collapsing box, so a leaving sprint takes its gap with it. */}
              <div className="pb-4 group-last/sprint:pb-0">{renderSprint(group)}</div>
            </CollapseIn>
          ))}
          {ungrouped.map(item => (
            <SprintDayReport
              key={item.date}
              workDay={item}
              todayDate={todayDate}
              showAddForm={showAddForm}
              showEditForm={showEditForm}
              showDeleteForm={showDeleteForm}
              isFinished={finished.has(item.date)}
              isFull={full?.has(item.date)}
              onSetFinished={
                onSetFinished && canUndo(item.date) ? isFinished => onSetFinished(item.date, isFinished) : undefined
              }
            />
          ))}
        </>
      )}
      {/* Outside the empty-state branch on purpose: a year you logged nothing in still needs its pager. */}
      {pager}
    </div>
  );
};

export default SprintOverview;
